import React, {
  Context,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SecureLS from 'secure-ls';
import { useUserSFOwner, useUserInfos } from 'views/auth/hook/auth_hook';

import { GetSFUserResponse, User } from 'types/User';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { App } from 'antd';
import BaseService from 'services/api/BaseService';
import * as Userpilot from 'tools/Userpilot';
import { AccountType, PartenaireType } from 'enums/accountTypeEnum';
import { Account } from 'types/UserInfos';

const ls = new SecureLS({ encodingType: 'aes' });

interface AuthPayload {
  token: string;
  user: User;
}

export interface AuthContextType {
  auth?: AuthPayload;
  owner?: GetSFUserResponse;
  ownerIsLoading?: boolean;
  userInfosIsLoading: boolean;
  login: (auth: AuthPayload) => void;
  logout: () => void;
  user?: User;
  account?: Account;
  accountType?: AccountType;
  isConcessionnaire: boolean;
  isEntrepriseTravaux: boolean;
  isBailleurSocial: boolean;
  isBureauEtude: boolean;
  isLeadReferent: boolean;
  /** Partenaire Lead sans aucun accès CEE (ETX, BS, Concessionnaire). */
  isLeadOnly: boolean;
  /** Partenaire Lead SANS Partenaire CEE ETX — navigation restreinte */
  isLeadReferentOnly: boolean;
  /** @deprecated Utiliser userInfosIsLoading */
  isAccountTypeLoading: boolean;
}

export const AuthContext: Context<AuthContextType> =
  React.createContext<AuthContextType>({
    auth: undefined,
    owner: undefined,
    ownerIsLoading: false,
    userInfosIsLoading: true,
    login: () => {},
    logout: () => {},
    user: undefined,
    account: undefined,
    accountType: undefined,
    isConcessionnaire: false,
    isEntrepriseTravaux: false,
    isBailleurSocial: false,
    isBureauEtude: false,
    isLeadReferent: false,
    isLeadOnly: false,
    isLeadReferentOnly: false,
    isAccountTypeLoading: false,
  });

interface AuthContextProviderProps {
  children: React.ReactNode;
}

function getUser(): User {
  try {
    return ls.get('user');
  } catch {
    return {} as User;
  }
}

export const getToken: () => string = () => {
  try {
    return ls.get('token') ? ls.get('token') : undefined;
  } catch {
    return '';
  }
};

export function getTokenExpiration(): number {
  const token = getToken();
  if (!token) {
    return 0;
  }
  const jwtPayload = JSON.parse(atob(token.split('.')[1]));
  return jwtPayload.exp * 1000;
}

export const AuthContextProvider = ({ children }: AuthContextProviderProps) => {
  const queryClient = useQueryClient();
  const refreshTokenTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const user = useMemo(getUser, []);
  const token = useMemo(getToken, []);

  const initialAuth = user && token ? { user, token } : undefined;

  const [auth, setAuth] = useState<AuthContextType['auth']>(initialAuth);

  const isAuthenticated = !!auth;

  const { data: owner, isLoading: ownerIsLoading } =
    useUserSFOwner(isAuthenticated);
  const { data: userInfos, isLoading: userInfosIsLoading } =
    useUserInfos(isAuthenticated);

  // Connexion
  const login: AuthContextType['login'] = (auth) => {
    setAuth(auth);
    ls.set('token', auth?.token);
    ls.set('user', auth?.user);
    queryClient.invalidateQueries();
    startTokenKeepAlive();
  };

  // Déconnexion
  const logout = () => {
    stopTokenKeepAlive();
    // Révocation serveur du JWT (best-effort) tant que le token est encore là,
    // puis purge du stockage local.
    void BaseService.logout();
    setAuth(undefined);
    queryClient.clear();
    ls.remove('token');
    ls.remove('user');
  };

  const startTokenKeepAlive = async () => {
    // Renouvelle le token 20 secondes avant son expiration
    const renewAfter = getTokenExpiration() - new Date().getTime() - 20000;
    clearTimeout(refreshTokenTimeout.current);
    refreshTokenTimeout.current = setTimeout(() => {
      checkAndRenewToken();
    }, renewAfter);
  };

  const stopTokenKeepAlive = () => {
    clearTimeout(refreshTokenTimeout.current);
  };

  const checkAndRenewToken = async () => {
    const hasExistingToken = !!getToken();
    const ttl = getTokenExpiration() - new Date().getTime();

    // Session expirée (on considère qu'on n'aura pas le temps de renouveler le token en moins de 10s)
    if (ttl <= 10000) {
      logout();
      // Envoie une notification de session expirée uniquement s'il existait précédemment un token
      if (hasExistingToken) {
        notification.info({
          message: t('global:session_expired'),
        });
      }
    } else {
      // Renouvelle le token
      stopTokenKeepAlive();
      await BaseService.renewToken();
      // Relance le keepalive avec un délai au cas où le renouvellement aurait échoué afin d'éviter de spammer le back-end
      setTimeout(() => {
        if (getToken()) {
          startTokenKeepAlive();
        }
      }, 2000);
    }
  };

  useEffect(() => {
    checkAndRenewToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      Userpilot.identify(user);
    }
  }, [user]);

  const isConcessionnaire = useMemo(() => {
    const types = userInfos?.account?.typesPartenaire;
    if (types?.includes(PartenaireType.Concessionnaire)) return true;
    return userInfos?.account?.recordType === AccountType.Concessionnaire;
  }, [userInfos]);

  const isEntrepriseTravaux = useMemo(() => {
    const types = userInfos?.account?.typesPartenaire;
    if (types?.length) return types.includes(PartenaireType.ETX);
    return userInfos?.account?.recordType === AccountType.EntrepriseTravaux;
  }, [userInfos]);

  const isBailleurSocial = useMemo(() => {
    const types = userInfos?.account?.typesPartenaire;
    if (types?.length) return types.includes(PartenaireType.BailleurSocial);
    return userInfos?.account?.recordType === AccountType.BailleurSocial;
  }, [userInfos]);

  const isBureauEtude = useMemo(
    () => userInfos?.account?.recordType === AccountType.BureauEtude,
    [userInfos],
  );

  const isLeadReferent = useMemo(
    () =>
      !!userInfos?.account?.typesPartenaire?.includes(
        PartenaireType.LeadReferent,
      ),
    [userInfos],
  );

  const isLeadOnly = useMemo(
    () =>
      isLeadReferent &&
      !isEntrepriseTravaux &&
      !isBailleurSocial &&
      !isConcessionnaire,
    [isLeadReferent, isEntrepriseTravaux, isBailleurSocial, isConcessionnaire],
  );

  const isLeadReferentOnly = useMemo(() => {
    const types = userInfos?.account?.typesPartenaire;
    if (!types?.includes(PartenaireType.LeadReferent)) return false;
    const otherPartnerTypes = types.filter(
      (t) => t !== PartenaireType.LeadReferent,
    );
    return otherPartnerTypes.length === 0;
  }, [userInfos]);

  const account = userInfos?.account;
  const accountType = userInfos?.account?.recordType;

  return (
    <AuthContext.Provider
      // eslint-disable-next-line @eslint-react/no-unstable-context-value
      value={{
        auth,
        login,
        logout,
        owner,
        ownerIsLoading,
        userInfosIsLoading,
        user,
        account,
        accountType,
        isConcessionnaire,
        isEntrepriseTravaux,
        isBailleurSocial,
        isBureauEtude,
        isLeadReferent,
        isLeadOnly,
        isLeadReferentOnly,
        isAccountTypeLoading: userInfosIsLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext: () => AuthContextType = () =>
  useContext(AuthContext);
